// Angular Files
import {
    Component,
    HostListener,
    computed,
    effect,
    signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';

// Angular Material Files
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

// Component Files
import { MAX_SURVEY_COMMENT_LENGTH, SCALE_RANGE, scoreBand } from './engagement.models';
import { TellerOnlineSurveyService } from './survey.service';

const THANKS_MS = 2500;
const CSAT_FACES = [
    'sentiment_very_dissatisfied',
    'sentiment_dissatisfied',
    'sentiment_neutral',
    'sentiment_satisfied',
    'sentiment_very_satisfied'
];
const ARROW_STEPS: Record<string, number> = {
    ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1
};

@Component({
    selector: 'teller-online-survey-card',
    imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule],
    templateUrl: './survey-card.component.html',
    styleUrls: ['./survey-card.component.scss']
})
export class TellerOnlineSurveyCardComponent {
    readonly maxComment = MAX_SURVEY_COMMENT_LENGTH;

    readonly faces = CSAT_FACES;

    readonly score = signal<number | null>(null);

    /** The follow-up question for the chosen score; the comment box only appears with it. */
    readonly followUp = computed(() => {
        const survey = this.surveyService.current();
        const score = this.score();

        return survey && score !== null
            ? survey.followUps[scoreBand(survey.scale, score)]
            : null;
    });

    comment = '';

    constructor(public surveyService: TellerOnlineSurveyService) {
        // Focus stays where the user is: the card must not interrupt what they were doing
        effect(() => {
            if (this.surveyService.current()) {
                this.score.set(null);
                this.comment = '';
            }
        });
        effect(() => {
            if (this.surveyService.submitted()) {
                setTimeout(() => this.surveyService.close(), THANKS_MS);
            }
        });
    }

    /** Scores from the scale's lowest to highest, for the button row. */
    public get scores(): number[] {
        const scale = this.surveyService.current()?.scale;
        if (!scale) return [];

        const { min, max } = SCALE_RANGE[scale];

        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
    }

    @HostListener('keydown.escape')
    onKeydown_dismiss(): void {
        this.surveyService.dismiss();
    }

    onClick_score(value: number): void {
        this.score.set(value);
    }

    // Arrow keys move the choice like a native radio group
    onKeydown_scoreArrow(event: KeyboardEvent): void {
        const step = ARROW_STEPS[event.key];
        if (!step) return;

        event.preventDefault();
        const all = this.scores;
        const current = this.score() ?? (step > 0 ? all[0] - 1 : all[all.length - 1] + 1);
        const next = Math.min(all[all.length - 1], Math.max(all[0], current + step));
        this.onClick_score(next);
        const buttons = (event.currentTarget as HTMLElement)
            .querySelectorAll<HTMLElement>('[role="radio"]');
        buttons[all.indexOf(next)]?.focus();
    }

    onClick_submit(): void {
        const score = this.score();
        if (score !== null) this.surveyService.submit(score, this.comment);
    }

    onClick_dismiss(): void {
        this.surveyService.dismiss();
    }
}
